import Phaser from "phaser";
import type { GameContext } from "../GameContext";
import type { CharacterSummary } from "../controllers/CharacterSelectController";
import { WorldRenderer } from "../render/WorldRenderer";
import type { StateSyncPayload, EntityUpdatePayload } from "@aetheria/shared-game";
import type { ServerMessage } from "../network/types";
import { WorldHud } from "../ui/panels/WorldHud";
import { CombatHud } from "../ui/panels/CombatHud";
import { InventoryGrid } from "../ui/panels/InventoryGrid";
import { EquipmentPanel } from "../ui/panels/EquipmentPanel";
import { CharacterSheet } from "../ui/panels/CharacterSheet";
import { SkillTree } from "../ui/panels/SkillTree";
import { JobAdvancementPanel } from "../ui/panels/JobAdvancementPanel";
import { EconomyPanel } from "../ui/panels/EconomyPanel";
import { WorldMapPanel } from "../ui/panels/WorldMapPanel";

interface WorldSceneData {
  character: CharacterSummary;
}

/**
 * The in-world scene per Part 3 (M16): hosts WorldRenderer plus every UI
 * panel (CharacterSheet, SkillTree, EquipmentPanel, JobAdvancementPanel,
 * WorldHud, CombatHud, InventoryGrid, EconomyPanel). All server messages
 * arrive via ctx.network's emitter (2.1's "never around it" rule) -- this
 * scene never touches WebSocket/fetch directly.
 */
export class WorldScene extends Phaser.Scene {
  private worldRenderer!: WorldRenderer;
  private character!: CharacterSummary;
  private unsubscribers: Array<() => void> = [];

  private hud!: WorldHud;
  private combatHud!: CombatHud;
  private inventoryGrid!: InventoryGrid;
  private equipmentPanel!: EquipmentPanel;
  private characterSheet!: CharacterSheet;
  private skillTree!: SkillTree;
  private jobAdvancementPanel!: JobAdvancementPanel;
  private economyPanel!: EconomyPanel;
  private worldMapPanel!: WorldMapPanel;
  private currentTargetId: string | null = null;
  private currentTargetMaxHp: number | null = null;

  constructor() {
    super("WorldScene");
  }

  private get ctx(): GameContext {
    return this.game.registry.get("ctx") as GameContext;
  }

  create(data: WorldSceneData): void {
    this.character = data.character;
    this.worldRenderer = new WorldRenderer(this);

    // Real character art for the 4 Tier-1 classes that have it (second
    // art delivery) — everyone else still gets WorldRenderer's generated
    // colored-circle placeholder (see its own resolveTextureKey comment).
    // Loaded here rather than BootScene since these are world-specific,
    // not needed until a player actually enters a zone.
    for (const job of ["blademaster", "ranger", "arcanist", "sentinel"]) {
      this.load.image(`player_${job}`, `/art/portraits/world-icon-${job}.png`);
    }
    this.load.start();

    this.unsubscribers.push(
      this.ctx.network.on("ws:message", (msg) => this.handleServerMessage(msg)),
      this.ctx.network.on("ws:close", () => this.hud?.setConnectionStatus("disconnected")),
      this.ctx.network.on("ws:reconnecting", () => this.hud?.setConnectionStatus("reconnecting")),
      this.ctx.network.on("ws:open", () => this.hud?.setConnectionStatus("connected"))
    );

    this.buildUi();
    this.buildInputHandlers();
    this.buildTargeting();
  }

  /**
   * Wires WorldRenderer's click reporting into actual targeting: clicking
   * a mob selects it as the combat target (shows CombatHud, per its own
   * "always-on overlay... visibility toggled internally" design — this is
   * that toggle). Clicking a player/npc is a no-op for now — only mobs
   * are valid combat targets; this doesn't build a trade/inspect flow.
   */
  private buildTargeting(): void {
    this.worldRenderer.onEntityClick((entity) => {
      if (entity.kind !== "mob") return;
      this.currentTargetId = entity.entityId;
      this.currentTargetMaxHp = entity.maxHp ?? null;
      this.combatHud.setTarget({
        entityId: entity.entityId,
        name: entity.name,
        hp: entity.hp ?? 0,
        maxHp: entity.maxHp ?? 0,
      });
    });

    this.worldRenderer.onEntityRemoved((entityId) => {
      if (entityId !== this.currentTargetId) return;
      this.currentTargetId = null;
      this.currentTargetMaxHp = null;
      this.combatHud.setTarget(null);
    });
  }

  private buildUi(): void {
    const root = document.createElement("div");
    root.className = "world-ui-root";
    this.add.dom(0, 0, root).setOrigin(0, 0);

    this.hud = new WorldHud(root, this.ctx, this.character, {
      onTogglePanel: (panel) => this.togglePanel(panel),
    });
    this.combatHud = new CombatHud(root, this.ctx);
    this.inventoryGrid = new InventoryGrid(root, this.ctx);
    this.equipmentPanel = new EquipmentPanel(root, this.ctx, this.character.id);
    this.characterSheet = new CharacterSheet(root, this.ctx, this.character.id);
    this.skillTree = new SkillTree(root, this.ctx, this.character.id);
    this.jobAdvancementPanel = new JobAdvancementPanel(root, this.ctx, this.character.id);
    this.economyPanel = new EconomyPanel(root, this.ctx, this.character.id);
    this.worldMapPanel = new WorldMapPanel(root, this.ctx, this.character);

    for (const panel of [
      this.inventoryGrid,
      this.equipmentPanel,
      this.characterSheet,
      this.skillTree,
      this.jobAdvancementPanel,
      this.economyPanel,
      this.worldMapPanel,
    ]) {
      panel.hide();
    }
  }

  private togglePanel(panel: "inventory" | "equipment" | "character" | "skills" | "job" | "economy" | "worldmap"): void {
    const map = {
      inventory: this.inventoryGrid,
      equipment: this.equipmentPanel,
      character: this.characterSheet,
      skills: this.skillTree,
      job: this.jobAdvancementPanel,
      economy: this.economyPanel,
      worldmap: this.worldMapPanel,
    } as const;
    map[panel].toggle();
  }

  private buildInputHandlers(): void {
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      // Client only ever SENDS an intent; the server is the sole authority
      // on whether the move is valid (2.8: "All movement is
      // server-validated ... against a per-connection ViolationTracker").
      this.ctx.network.send({ type: "move", payload: { x: pointer.worldX, y: pointer.worldY } });
    });
  }

  private handleServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case "state_sync": {
        const sync = msg.payload as StateSyncPayload;
        this.worldRenderer.applyStateSync(sync);
        if (this.currentTargetId) {
          const targetEntity = sync.entities.find((e) => e.entityId === this.currentTargetId);
          if (targetEntity && targetEntity.hp !== undefined && targetEntity.maxHp !== undefined) {
            this.currentTargetMaxHp = targetEntity.maxHp;
            this.combatHud.updateHpDisplay(targetEntity.hp, targetEntity.maxHp);
          }
          // Not found in this sync at all -> already handled by
          // WorldRenderer's onEntityRemoved callback (applyStateSync
          // above calls removeEntity for anything missing).
        }
        break;
      }
      case "entity_update": {
        const update = msg.payload as EntityUpdatePayload;
        this.worldRenderer.applyEntityUpdate(update);
        // CombatHud has its own HP bar, separate from WorldRenderer's
        // in-world mini bar over the sprite — both need to reflect real
        // damage. WorldRenderer already handles the sprite-removal side
        // of a defeat via onEntityRemoved (registered in buildTargeting).
        if (update.entityId === this.currentTargetId && update.hp !== undefined && this.currentTargetMaxHp !== null) {
          this.combatHud.updateHpDisplay(update.hp, this.currentTargetMaxHp);
        }
        break;
      }
      case "chat_message":
        this.hud.appendChatMessage(msg.payload as { from: string; text: string });
        break;
      case "pong":
        break; // handled inside NetworkManager itself
      default:
        break;
    }
  }

  shutdown(): void {
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
    this.worldRenderer.destroy();
  }
}
